import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../lib/i18n';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Calendar, MapPin, Truck, ChevronRight, ChevronDown, Clock, Loader2, List, LayoutGrid, ChevronLeft } from 'lucide-react';
import { api } from '../../lib/auth';
import { formatLocalISO, todayLocalISO, mondayOf, addDays } from '../../lib/dateUtils';

const FALLBACK_DAYS = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const FALLBACK_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/** Helper: look up a short-day label from the current i18n namespace. */
function dayShort(t, dow) {
  const v = t(`dashboard.daysShort.${dow}`);
  return (typeof v === 'string' && v && !v.startsWith('dashboard.')) ? v : FALLBACK_DAYS[dow];
}
/** Helper: short-month label. */
function monthShort(t, m) {
  const v = t(`dashboard.monthsShort.${m}`);
  return (typeof v === 'string' && v && !v.startsWith('dashboard.')) ? v : FALLBACK_MONTHS[m];
}

/** Return Monday 00:00 of the week containing `d` (local time). We roll
 *  back JS's Sunday-is-0 quirk so the strip always starts on Mon.
 *  (delegates to lib/dateUtils.mondayOf) */

function isoDate(d) {
  // lib/dateUtils.formatLocalISO ile aynı: yerel YYYY-MM-DD. UTC dönüşümünden
  // kaçınır; aksi halde Brussels gece yarısı atamalar bir gün öne kayar.
  return formatLocalISO(d);
}

/** Employee-facing planning widget.
 *
 *  Each driver sees:
 *    - today's assignment banner (if any) — big + visual
 *    - the next 7 days of upcoming assignments
 *    - a collapsible "Geçmiş" list with the last 30 days
 *
 *  Fully noop-safe: if the user has no assignments at all we render nothing
 *  instead of an empty card — avoids drawing visual weight on the dashboard
 *  for admins / non-driver roles who happen to have an employee_id but
 *  never get scheduled.
 */
export default function EmployeePlanningWidget() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  // Yerel tarih (UTC değil)
  const [today, setToday] = useState(() => todayLocalISO());
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  // View toggle: "list" (day-by-day feed, default) or "week" (5- or 7-day
  // horizontal strip starting on Monday of the anchored week). We persist
  // the chosen view so it survives reloads — driver-friendly.
  const [view, setView] = useState(() => {
    try { return localStorage.getItem('planningWidget.view') || 'week'; } catch { return 'week'; }
  });
  useEffect(() => { try { localStorage.setItem('planningWidget.view', view); } catch {} }, [view]);
  // Week view: how many days to show (5=workweek, 7=full) + current
  // Monday anchor. Start date is always a Monday so the strip reads
  // Pzt → Cum / Pzt → Paz regardless of today's weekday.
  const [weekDays, setWeekDays] = useState(7);
  const [weekAnchor, setWeekAnchor] = useState(() => mondayOf(new Date()));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/planning/my-assignments?days_back=30&days_ahead=60&limit=200');
        if (cancelled) return;
        setItems(res.data?.data || []);
        if (res.data?.window?.today) setToday(res.data.window.today);
      } catch { /* silent — widget is optional */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Bucket by temporal relation to today. Sorted ascending within each
  // bucket so the UI reads from soonest to latest; history is DESC so
  // most-recent is first.
  const { todayRow, upcoming, history } = useMemo(() => {
    const byDate = [...items].sort((a, b) => (a.assignment_date || '').localeCompare(b.assignment_date || ''));
    const todayRows = byDate.filter((i) => i.assignment_date === today);
    const upcomingRows = byDate.filter((i) => i.assignment_date > today).slice(0, 7);
    const historyRows = byDate.filter((i) => i.assignment_date < today).reverse().slice(0, 30);
    return { todayRow: todayRows[0] || null, upcoming: upcomingRows, history: historyRows };
  }, [items, today]);

  if (loading) {
    return (
      <Card className="p-3 flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {t('dashboard.planningLoading')}
      </Card>
    );
  }

  // Nothing for this user — render nothing (avoids dashboard clutter).
  if (items.length === 0) return null;

  return (
    <Card className="overflow-hidden" data-testid="employee-planning-widget">
      <div className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 flex-1">{t('dashboard.myPlanning')}</h3>
        <span className="text-[11px] text-slate-500 hidden sm:inline">
          {t('dashboard.upcomingCount', { count: upcoming.length })} · {t('dashboard.historyCount', { count: history.length })}
        </span>
        {/* view toggle: list ↔ week strip */}
        <div className="inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
          <button
            onClick={() => setView('list')}
            className={`px-2 py-1 text-[10px] inline-flex items-center gap-1 ${view === 'list' ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
            data-testid="planning-view-list"
            title={t('dashboard.viewList')}
          >
            <List className="h-3 w-3" /> {t('dashboard.viewList')}
          </button>
          <button
            onClick={() => setView('week')}
            className={`px-2 py-1 text-[10px] inline-flex items-center gap-1 ${view === 'week' ? 'bg-emerald-500 text-white' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
            data-testid="planning-view-week"
            title={t('dashboard.viewWeek')}
          >
            <LayoutGrid className="h-3 w-3" /> {t('dashboard.viewWeek')}
          </button>
        </div>
      </div>

      {/* ─── WEEK STRIP VIEW ─── */}
      {view === 'week' ? (
        <WeekStrip
          items={items}
          today={today}
          weekAnchor={weekAnchor}
          weekDays={weekDays}
          t={t}
          onPrev={() => setWeekAnchor(addDays(weekAnchor, -7))}
          onNext={() => setWeekAnchor(addDays(weekAnchor, 7))}
          onThisWeek={() => setWeekAnchor(mondayOf(new Date()))}
          onToggleDays={() => setWeekDays((d) => (d === 7 ? 5 : 7))}
        />
      ) : (
        <>
          {/* Today's hero banner */}
          {todayRow ? (
        <div className="px-4 py-3 bg-emerald-500/10 border-b border-emerald-200/50 dark:border-emerald-900/50 flex items-start gap-3" data-testid="planning-today">
          <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: todayRow.company_color || '#10b981' }}>
            <Truck className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-semibold">{t('dashboard.today')}</div>
            <div className="text-sm font-semibold truncate">
              {todayRow.route_code ? <span className="font-mono">{todayRow.route_code}</span> : ''}
              {todayRow.route_name ? <span className="ml-1 text-slate-600 dark:text-slate-300">· {todayRow.route_name}</span> : null}
            </div>
            <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
              {todayRow.company_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {todayRow.company_name}</span>}
              {todayRow.depot_name && <span>• {todayRow.depot_name}</span>}
              {todayRow.vehicle_plate && <span className="font-mono">🚛 {todayRow.vehicle_plate}</span>}
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-2.5 text-[11px] italic text-slate-400 border-b border-slate-100 dark:border-slate-800" data-testid="planning-today-empty">
          {t('dashboard.noAssignmentToday')}
        </div>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div className="px-2 py-2 space-y-1" data-testid="planning-upcoming-list">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 px-2 pt-1">{t('dashboard.nextSevenDays')}</div>
          {upcoming.map((row) => <AssignmentRow key={row.id} row={row} t={t} />)}
        </div>
      )}

      {/* History toggle */}
      {history.length > 0 && (
        <div className="border-t border-slate-100 dark:border-slate-800">
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-2 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/40"
            data-testid="planning-history-toggle"
          >
            {showHistory ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <Clock className="h-3.5 w-3.5" />
            <span>{t('dashboard.historyWith', { count: history.length })}</span>
          </button>
          {showHistory && (
            <div className="px-2 pb-2 space-y-1">
              {history.map((row) => <AssignmentRow key={row.id} row={row} dim t={t} />)}
            </div>
          )}
        </div>
      )}
        </>
      )}
    </Card>
  );
}

function AssignmentRow({ row, dim, t }) {
  const d = row.assignment_date ? new Date(row.assignment_date + 'T00:00:00') : null;
  const dayLabel = d ? `${d.getDate()} ${monthShort(t, d.getMonth())}` : row.assignment_date;
  const weekday = d ? dayShort(t, d.getDay()) : '';
  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${dim ? 'opacity-60' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
      data-testid={`planning-row-${row.id}`}
    >
      <div className="w-12 shrink-0 text-center">
        <div className="text-[10px] uppercase text-slate-400">{weekday}</div>
        <div className="font-semibold text-slate-700 dark:text-slate-200 tabular-nums">{dayLabel}</div>
      </div>
      <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: row.route_color || row.company_color || '#94a3b8' }} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 dark:text-slate-200 truncate">
          {row.route_code ? <span className="font-mono">{row.route_code}</span> : null}
          {row.route_name ? <span className="ml-1 font-normal text-slate-600 dark:text-slate-400">{row.route_name}</span> : null}
        </div>
        <div className="text-[10px] text-slate-500 truncate">
          {[row.company_name, row.depot_name].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>
      {row.vehicle_plate && (
        <Badge variant="outline" className="text-[9px] font-mono shrink-0">{row.vehicle_plate}</Badge>
      )}
    </div>
  );
}

/**
 * Horizontal week strip — one column per day, Pzt→Cum (5) or Pzt→Paz (7).
 *
 * Days with an assignment show a color-coded card (firma/route renginden
 * ince sol çubuk + route kodu + depot). Days without are faded "—" cells.
 * The current day pulses with an emerald ring so the driver can instantly
 * locate where in the week they are.
 */
function WeekStrip({ items, today, weekAnchor, weekDays, onPrev, onNext, onThisWeek, onToggleDays, t }) {
  const days = Array.from({ length: weekDays }, (_, i) => addDays(weekAnchor, i));
  const weekEnd = days[days.length - 1];
  const itemsByDate = useMemo(() => {
    const map = {};
    for (const row of items) {
      const d = row.assignment_date;
      if (!d) continue;
      (map[d] = map[d] || []).push(row);
    }
    return map;
  }, [items]);

  const fmtHeader = (d) => `${d.getDate()} ${monthShort(t, d.getMonth())}`;

  return (
    <div className="p-3 space-y-2" data-testid="planning-week-strip">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5">
        <button onClick={onPrev} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" data-testid="planning-week-prev" title={t('dashboard.prevWeek')}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 text-center">
          <div className="text-[11px] text-slate-400 uppercase tracking-wide">{t('dashboard.week')}</div>
          <div className="text-xs font-semibold text-slate-700 dark:text-slate-200 tabular-nums">
            {fmtHeader(weekAnchor)} – {fmtHeader(weekEnd)}
          </div>
        </div>
        <button onClick={onNext} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" data-testid="planning-week-next" title={t('dashboard.nextWeek')}>
          <ChevronRight className="h-4 w-4" />
        </button>
        <button onClick={onThisWeek} className="ml-1 px-2 py-0.5 text-[10px] rounded border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-emerald-600 hover:border-emerald-200" data-testid="planning-week-today">
          {t('dashboard.today')}
        </button>
        <button onClick={onToggleDays} className="px-2 py-0.5 text-[10px] rounded border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" title={t('dashboard.weekDaysToggle')}>
          {weekDays === 7 ? t('dashboard.sevenDaysShort') : t('dashboard.fiveDaysShort')}
        </button>
      </div>

      {/* Strip — horizontal scroll on small screens so 7 cards stay readable */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
        {days.map((d) => {
          const key = isoDate(d);
          const rows = itemsByDate[key] || [];
          const isToday = key === today;
          const isSunday = d.getDay() === 0;
          return (
            <div
              key={key}
              className={`min-w-[120px] flex-1 rounded-lg border p-2 ${isToday
                ? 'border-emerald-400 ring-2 ring-emerald-300/40 bg-emerald-50/40 dark:bg-emerald-950/20'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
              }`}
              data-testid={`planning-week-day-${key}`}
            >
              <div className={`flex items-center justify-between text-[10px] ${isSunday ? 'text-rose-500' : 'text-slate-400'}`}>
                <span className="uppercase font-semibold">{dayShort(t, d.getDay())}</span>
                <span className="tabular-nums">{d.getDate()}</span>
              </div>
              {rows.length === 0 ? (
                <div className="mt-2 text-center text-[10px] text-slate-300 dark:text-slate-700 py-2">—</div>
              ) : (
                <div className="mt-1.5 space-y-1">
                  {rows.map((row) => (
                    <div
                      key={row.id}
                      className="text-[10px] rounded bg-slate-50 dark:bg-slate-800/50 border-l-2 px-1.5 py-1"
                      style={{ borderLeftColor: row.route_color || row.company_color || '#64748b' }}
                      title={[row.route_code, row.route_name, row.company_name, row.depot_name].filter(Boolean).join(' · ')}
                    >
                      <div className="font-mono font-semibold text-slate-700 dark:text-slate-200 truncate">
                        {row.route_code || '—'}
                      </div>
                      <div className="text-slate-500 truncate">
                        {row.depot_name || row.company_name || ''}
                      </div>
                      {row.vehicle_plate && (
                        <div className="text-[9px] font-mono text-slate-400 truncate mt-0.5">🚛 {row.vehicle_plate}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

